/* iceberg-ui */
import {
    Card,
    CardBody,
    CardHeader,
    CardHeading,
    Flex,
    IconWithVariant
} from '@auviknetworks/iceberg-ui';

/* MUI */
import { Box, Grid, List, ListItem, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ApplicationBubbles } from 'application/Shared/Components/Avatars';
import { SaasOpsIsConnectedChip } from 'application/Shared/Components/Client/SaasOps/SaasOpsIsConnectedChip';

/* Constants */
import { FontSizes } from 'application/Shared/Constants';
import { Application } from 'application/Shared/types/application';
import { User } from 'application/Shared/types/user';

/* Types */
// import { User } from 'application/Shared/types/user';

/* Objects */
import {
    // User as UserContext,
    UserIconAndName,
    useUserContext
} from 'objects/User';

/* React */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { mockConnectionData } from '../mockData';
import { WorkatoActions } from '../types';
import { useConnectionsAndRecipesContext } from './ConnectionsAndRecipesContext';
import {
    SaasOpsActionItemStatus,
    SaasOpsActionItemsState,
    SaasOpsActionStatus
} from './types';

const IntegrationDetails = ({
    action,
    currentAppId,
    saasOpsActionItemsState
}: {
    action: WorkatoActions;
    currentAppId: string;
    saasOpsActionItemsState: SaasOpsActionItemsState;
}) => {
    const { t } = useTranslation();
    const { connectionsByProvider } = useConnectionsAndRecipesContext();

    const currentItem = saasOpsActionItemsState.find(
        (app) => app.applicationId === currentAppId
    );

    const workatoProvider = currentItem?.workatoProvider;

    const connection = connectionsByProvider[workatoProvider!];

    const connectionStatus = connection?.authorization_status;
    const isConnected = connectionStatus === 'success';
    const lostConnection = connectionStatus === 'connectionError';

    const onboardUrl =
        'https://support.auvik.com/hc/en-us/articles/35177796950932-Onboarding-a-User-using-SaaS-Ops-Integrations';
    const offboardUrl =
        'https://support.auvik.com/hc/en-us/articles/35177627381396-Offboarding-a-User-using-SaaS-Ops-Integrations';

    const connectionData = mockConnectionData.find(
        (conn) => conn.name === workatoProvider
    );

    return (
        <Flex sx={{ flexWrap: 'wrap', gap: '16px' }}>
            <SaasOpsIsConnectedChip
                isConnected={isConnected}
                lostConnection={lostConnection}
            />
            {connectionData?.link && (
                <Link
                    to={connectionData?.link}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                >
                    <Typography
                        color="text.link.text"
                        sx={{
                            '&:hover': {
                                color: 'text.link.hover'
                            }
                        }}
                    >
                        {t('SaasOps.integrationCards.moreDetails', {
                            displayName: connectionData.displayName
                        })}
                    </Typography>
                </Link>
            )}
            <Link
                to={
                    action === WorkatoActions.onboard ? onboardUrl : offboardUrl
                }
                target="_blank"
                rel="noopener noreferrer nofollow"
            >
                <Typography
                    color="text.link.text"
                    sx={{
                        '&:hover': {
                            color: 'text.link.hover'
                        }
                    }}
                >
                    {t('SaasOps.howTo', {
                        action
                    })}
                </Typography>
            </Link>
            {saasOpsActionItemsState.length > 1 && (
                <>
                    <Flex sx={{ gap: '8px' }}>
                        <IconWithVariant
                            variant="primary"
                            src="Lighting"
                            size={FontSizes[3]}
                        />
                        <Typography> {t('Common.tips')}</Typography>
                    </Flex>
                    <List
                        style={{
                            marginTop: '0px',
                            paddingBottom: '0px',
                            paddingLeft: '16px',
                            paddingTop: '0px'
                        }}
                    >
                        <ListItem
                            className="li-purple"
                            style={{
                                paddingTop: '0px'
                            }}
                        >
                            <Typography>
                                {t('SaasOps.quickTips.tipOne')}
                            </Typography>
                        </ListItem>
                        <ListItem className="li-purple">
                            <Typography>
                                {t('SaasOps.quickTips.tipTwo')}
                            </Typography>
                        </ListItem>
                        <ListItem
                            className="li-purple"
                            style={{
                                paddingBottom: '0px'
                            }}
                        >
                            <Typography>
                                {t('SaasOps.quickTips.tipThree')}
                            </Typography>
                        </ListItem>
                    </List>
                </>
            )}
        </Flex>
    );
};

/**
 * SupportingDetailsSidePanel Component
 *
 * Displays user details and SaaS operations status in a side panel
 *
 * @component
 * @param {Object} props
 * @param {WorkatoActions} props.action - The type of SaaS operation being performed
 * @param {string} props.currentAppId - The ID of the current application being processed
 * @param {SaasOpsActionStatus} props.overallSaasOpsActionStatus - Current status of the overall operation
 * @param {SaasOpsActionItemsState} props.saasOpsActionItemsState - State of applications being processed
 * @returns {JSX.Element} Side panel with user details and operation status
 */

export const SupportingDetailsSidePanel = ({
    action,
    currentAppId,
    overallSaasOpsActionStatus,
    saasOpsActionItemsState
}: {
    action: WorkatoActions;
    currentAppId: string;
    overallSaasOpsActionStatus: SaasOpsActionStatus;
    saasOpsActionItemsState: SaasOpsActionItemsState;
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const context = useUserContext();

    const user = (context?.user || {}) as User;

    // Count applications where the SaaS Ops process has been completed
    const completedAppsCount = saasOpsActionItemsState.filter(
        (app) => app.status === SaasOpsActionItemStatus.Completed
    ).length;

    const nonRemovedItems = saasOpsActionItemsState.filter(
        (app) => app.status !== SaasOpsActionItemStatus.Removed
    );
    const totalNonRemovedItemsCount = nonRemovedItems.length;

    const apps = nonRemovedItems.map<Application>(
        (item) => item.recipe.application!
    );

    /**
     * Gets the appropriate status message based on the current operation status
     * @returns {string} Translated status message
     */
    const statusMessage = () => {
        if (overallSaasOpsActionStatus === SaasOpsActionStatus.Stopped)
            return t(`SaasOps.${action}.stopped`);
        if (overallSaasOpsActionStatus === SaasOpsActionStatus.InProgress)
            return t(`SaasOps.${action}.inProgress`);

        return t(`SaasOps.${action}.completed`);
    };

    return (
        <Grid
            item
            width="350px"
            minWidth="350px"
            maxWidth="350px"
            padding={3}
            sx={{
                backgroundColor: theme.palette.background.tertiary,
                overflowY: 'auto'
            }}
            flexGrow={1}
        >
            <Card
                mb="16px"
                variant="outlined"
                sx={{
                    backgroundColor: theme.palette.background.saasOpsUserDetails
                }}
            >
                <CardHeader
                    sx={{
                        padding: '16px',
                        paddingBottom: 0
                    }}
                >
                    <CardHeading>{t('Common.status')}</CardHeading>
                </CardHeader>
                <CardBody p={3}>
                    <Box mb={1}>
                        <Typography color="text.tertiary" fontWeight={600}>
                            {statusMessage()}
                        </Typography>
                    </Box>
                    {apps.length > 1 && (
                        <Box>
                            <Typography
                                fontSize={FontSizes[1]}
                                sx={{ overflowWrap: 'break-word' }}
                            >
                                {t(`SaasOps.${action}.appsCompleted`, {
                                    completedAppsCount,
                                    totalAppsCount: totalNonRemovedItemsCount
                                })}
                            </Typography>
                        </Box>
                    )}
                </CardBody>
            </Card>
            {apps.length > 1 && (
                <Card
                    mb="16px"
                    variant="outlined"
                    sx={{
                        backgroundColor:
                            theme.palette.background.saasOpsUserDetails
                    }}
                >
                    <CardHeader
                        sx={{
                            padding: '16px',
                            paddingBottom: 0
                        }}
                    >
                        <CardHeading>
                            {t('Common.total_applications')}
                        </CardHeading>
                    </CardHeader>
                    <CardBody p={3}>
                        <Flex
                            sx={{
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 2
                            }}
                        >
                            <Typography color="text.tertiary" fontWeight={600}>
                                {totalNonRemovedItemsCount}
                            </Typography>
                            <Box display="flex" justifyContent="center">
                                <ApplicationBubbles
                                    max={3}
                                    applications={apps}
                                />
                            </Box>
                        </Flex>
                    </CardBody>
                </Card>
            )}
            <Card
                variant="outlined"
                sx={{
                    backgroundColor: theme.palette.background.saasOpsUserDetails
                }}
            >
                <CardHeader
                    sx={{
                        padding: '16px',
                        paddingBottom: 0
                    }}
                >
                    <CardHeading>{t('Common.user_details')}</CardHeading>
                </CardHeader>
                <CardBody p={3}>
                    <Box mb={3}>
                        <Typography
                            data-testid="SupportingDetailsSidePanel-name"
                            fontSize={FontSizes[1]}
                            fontWeight={600}
                            mb="4px"
                        >
                            {t('Common.name')}
                        </Typography>
                        {/*
                            User context is higher up the component tree. Currently in SaasOpsActionStateBuilder.
                        */}
                        <UserIconAndName noTruncate />
                    </Box>

                    {user?.email && (
                        <Box>
                            <Typography
                                data-testid="SupportingDetailsSidePanel-email"
                                fontSize={FontSizes[1]}
                                fontWeight={600}
                            >
                                {t('Common.email')}
                            </Typography>
                            <Typography
                                fontSize={FontSizes[1]}
                                sx={{ overflowWrap: 'break-word' }}
                            >
                                {user?.email}
                            </Typography>
                        </Box>
                    )}
                </CardBody>
            </Card>
            <Card
                mt="16px"
                variant="outlined"
                sx={{
                    backgroundColor: theme.palette.background.saasOpsUserDetails
                }}
            >
                <CardHeader
                    sx={{
                        padding: '16px',
                        paddingBottom: 0
                    }}
                >
                    <CardHeading>{t('Common.integration_details')}</CardHeading>
                </CardHeader>
                <CardBody p={3}>
                    <IntegrationDetails
                        action={action}
                        currentAppId={currentAppId}
                        saasOpsActionItemsState={saasOpsActionItemsState}
                    />
                </CardBody>
            </Card>
        </Grid>
    );
};
